import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios from 'axios';
import ConfirmActionPopup from 'components/ConfirmActionPopup';
import { DropdownOption, mapToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import TextArea from 'components/final-form/TextArea';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContainer from 'components/layout/FormContainer';
import FormContent from 'components/layout/FormContent';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import PraxFormScrollableContainer from 'components/layout/PraxFormScrollableContainer';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import LoaderComponent from 'components/LoaderComponent';
import BillOptionsSegment from 'components/treatments/segments/BillOptionsSegment';
import DiagnosisSegment from 'components/treatments/segments/DiagnosisSegment';
import HospitalizationAndDoctorAccordion from 'components/treatments/segments/HospitalizationAndDoctorAccordion';
import PrescriptionsSegment from 'components/treatments/segments/PrescriptionsSegment';
import TreatmentCodesAccordion from 'components/treatments/segments/TreatmentCodesAccordion';
import TreatmentInfoSegment from 'components/treatments/segments/TreatmentInfoSegment';
import TreatmentPrescriptionSegment from 'components/treatments/segments/TreatmentPrescriptionSegment';
import UnableToWorkSegment from 'components/treatments/segments/UnableToWorkSegment';
import { EditTreatmentValues, TreatmentsConsumerRenderProps } from 'components/treatments/TreatmentsContext';
import { FormApi, MutableState, Tools } from 'final-form';
import arrayMutators from 'final-form-arrays'
import createDecorator from 'final-form-calculate';
import createPmedDecorator from 'final-form-calculate-pmed/decorator';
import moment from 'moment';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import 'react-virtualized/styles.css';
import { Button, Grid, Modal, RadioProps } from 'semantic-ui-react';
import { getAllCostCenters } from 'services/costCenterServices';
import { getAllPatientCostEstimationsByTariffId } from 'services/costEstimationServices';
import { findPatientById } from 'services/patientServices';
import { getAllTariffs } from 'services/tariffServices';
import { deleteTempTreatmentPrescription } from 'services/treatmentPrescriptionServices';
import { getAllTreatmentReasons } from 'services/treatmentReasonServices';
import {
  deleteTreatment,
  editTreatment,
  getPatientGuarantorForTariff,
  getTreatmentTariffs,
  insertTreatment,
  undeleteTreatment
} from 'services/treatmentServices';
import styled from 'styled-components';
import { BillReceiver, BillStatus } from 'ts-types/api.enums';
import {
  CodeDto,
  ConsultationDto,
  CostEstimationDto,
  TariffDto,
  TreatmentCodeDto,
  TreatmentDto,
  TreatmentPatientGuarantorRequest,
  TreatmentTariffDto,
  Unfit,
  UpsertTreatmentDto
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { isNotNull, noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';
import { isValueEmpty } from 'utils/validatorUtils';

const StyledNotesContainer = styled(PraxFormGrid)`
  &.ui.grid {
    margin-bottom: 0.5rem;
    margin-top: 0 !important;
    padding-top: 0.27rem;
    border-top: 1px solid rgba(34, 36, 38, .1);
  }
`

interface Props extends MessageSourceProps, AuthConsumerRenderProps, TreatmentsConsumerRenderProps {
  treatmentFormOpen: boolean,
  editingTreatment: boolean,
  toggleUpsertModal: () => void,
  currentPatientId: number,
  treatment: EditTreatmentValues,
  currentConsultations: Array<ConsultationDto>,
  selectedTreatment: TreatmentDto
}

interface State extends IndexSignature {
  tariffs: Array<TreatmentTariffDto | TariffDto>,
  reasons: Array<DropdownOption>,
  costCenters: Array<DropdownOption>,
  initialGuarantors: Array<DropdownOption>,
  initialDoctors: Array<DropdownOption>,
  costEstimations: Array<CostEstimationDto>,
  billReceivers: Array<RadioProps>,
  mtgNumber?: string,
  isPatientUnderaged: boolean,
  loadingMessage?: string,
  uploading: boolean,
  tempTreatmentId?: number,
  errorMessage?: string
}

interface Errors {
  representingDoctorId: string,
  referralDoctorId: string
}

const percents: Array<number> = [0, 25, 50, 75, 100];
const percent: Array<DropdownOption> = percents.map((percent: number, index: number) => ({
  key: index,
  text: percent.toString(),
  value: percent
}));

class TreatmentsForm extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    const {treatment, getMessage, currentPatientId} = this.props;

    const guarantors: Array<DropdownOption> = [];
    if (treatment && treatment.guarantorDto) {
      const {id, description} = treatment.guarantorDto;

      guarantors.push({
        key: id,
        text: description,
        value: id
      })
    }

    const doctors: Array<DropdownOption> = [];
    if (treatment && treatment.doctors) {
      treatment.doctors.forEach(doctor => {
        doctors.push({
          key: doctor.id,
          text: `${doctor.name} ${doctor.firstName} - ${doctor.city}`,
          value: doctor.id
        })
      })
    }

    const billReceivers = generateRadioButtonOptionsFromEnum(
        Object.values(BillReceiver), "billReceiver", getMessage);

    this.state = {
      tariffs: [],
      reasons: [],
      costCenters: [],
      costEstimations: [],
      initialGuarantors: guarantors,
      initialDoctors: doctors,
      billReceivers: billReceivers,
      isPatientUnderaged: false,
      loadingMessage: getMessage("treatmentForm.loading"),
      uploading: false,
      cancelTokenSource
    };

    const onFinally = () => {
      this.setState({
        loadingMessage: undefined
      })
    };

    const getTariffs = treatment && treatment.id != -1
        ? () => getTreatmentTariffs(treatment.id, cancelTokenSource)
        : () => getAllTariffs(cancelTokenSource);

    const methods: Array<any> = [
      getTariffs(),
      getAllTreatmentReasons(cancelTokenSource),
      getAllCostCenters(cancelTokenSource),
      findPatientById(cancelTokenSource, currentPatientId)
    ];
    Promise.all(methods)
        .then(responses => {

          const tariffs = responses[0];
          const reasons = responses[1];
          const costsCenters = responses[2];
          const patient = responses[3];

          const isPatientUnderaged = moment().diff(patient.birthDate, 'years') < 16;

          this.setState({
            tariffs: tariffs,
            tariffId: tariffs.length ? tariffs[0].id : -1,
            reasons: mapToDropdownOptionArray(reasons, "description"),
            costCenters: mapToDropdownOptionArray(costsCenters, "description"),
            isPatientUnderaged
          })

          if (tariffs.length) {
            const tariffId = treatment ? treatment.tariffId : tariffs[0].id;
            this.getPatientCostEstimations(tariffId);
          }

        })
        .catch(noop)
        .finally(onFinally);

    if (treatment) {
      const {unfit} = treatment;

      unfit.map((p: Unfit) => {
        const isMember = (percent: DropdownOption) => {
          return percent.value.toString() === p.percent.toString();
        };

        if (!percent.find(isMember)) {
          percent.push({
            key: p.percent,
            text: p.percent.toString(),
            value: p.percent
          });
        }
      });
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;

    this.mounted = false;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  getPatientCostEstimations = (tariffId: number) => {
    if (!this.mounted) {
      return;
    }

    const {currentPatientId, treatment} = this.props;
    const {cancelTokenSource} = this.state;

    this.setState({
      loadingTableData: true
    });

    getAllPatientCostEstimationsByTariffId(currentPatientId, tariffId, cancelTokenSource)
        .then(response => {

          const filteredCostEstimations =
              response.filter(ce => !ce.used || (treatment && treatment.costEstimationId && ce.id === treatment.costEstimationId));

          this.setState({
            costEstimations: filteredCostEstimations
          })
        })
        .catch(noop)
        .finally(() => {
          this.setState({
            loadingTableData: false
          })
        });
  };

  initialTreatmentValues = (): Partial<UpsertTreatmentDto> => {

    const {currentUser, currentPatientId, options: {workDate}} = this.props;
    const {tariffs, reasons, costCenters} = this.state;

    return {
      patientId: currentPatientId,
      treatmentDate: workDate,
      accidentDate: undefined,
      mandateId: currentUser!.userConfig.mandateId,
      bill: BillReceiver.PATIENT,
      guarantorDoctor: false,
      guarantorId: undefined,
      tariffId: tariffs.length > 0 ? tariffs[0].id : undefined,
      reasonId: reasons.length > 0 ? reasons[0].value : undefined,
      costCenterId: costCenters.length > 0 ? costCenters[0].value : undefined,
      costEstimationId: -1,
      docPrinted: true,
      hospitalizationFrom: "",
      hospitalizationTo: "",
      diagnosis: "",
      unfit: [],
      prescriptions: [],
      treatmentCodes: [],
      status: false
    }
  };

  billPeriodByTariff = createDecorator({
    field: 'tariffId',
    updates: async (selectedTariffId) => {

      const {tariffs, cancelTokenSource} = this.state;
      const {currentPatientId, treatment} = this.props;

      let billPeriod = 30;

      if (tariffs.length > 0) {
        const tariffIndex = tariffs.findIndex((tariff) => {
          return tariff.id === selectedTariffId;
        });

        billPeriod = tariffs[tariffIndex].billPeriod;
      } else if (treatment) {
        billPeriod = treatment.billPeriod;
      }

      const patientGuarantorRequest: TreatmentPatientGuarantorRequest = {
        patientId: currentPatientId,
        tariffId: selectedTariffId,
        treatmentDate: treatment ? treatment.treatmentDate : moment().format("YYYY-MM-DD")
      };

      if (treatment && treatment.tariffId == selectedTariffId) {
        return {}
      } else {
        try {
          const patientGuarantor =
              await getPatientGuarantorForTariff(patientGuarantorRequest, cancelTokenSource);
          return {
            billPeriod: billPeriod,
            guarantorId: patientGuarantor.guarantorId,
            mtgNumber: patientGuarantor.mtgNumber,
            costEstimationId: -1
          };
        } catch (e) {
          // do nothing
          return {};
        }
      }
    }
  });

  resetHospitalization = createDecorator({
    field: 'isHospitalized',
    updates: {
      hospitalizationFrom: (fieldValue, allValues?: Partial<UpsertTreatmentDto>) => {
        return !fieldValue || !allValues ? "" : allValues.hospitalizationFrom;
      },
      hospitalizationTo: (fieldValue, allValues?: Partial<UpsertTreatmentDto>) => {
        return !fieldValue || !allValues ? "" : allValues.hospitalizationTo;
      }
    }
  });

  resetHospitalizationTo = createDecorator({
    field: 'hospitalizationFrom',
    updates: {
      hospitalizationTo: (fieldValue, allValues?: Partial<UpsertTreatmentDto>) => {

        if (fieldValue && allValues) {

          const fromDate = fieldValue && new Date(fieldValue);
          const toDate = allValues.hospitalizationTo && new Date(allValues.hospitalizationTo);

          if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
            return fromDate;
          }

          return allValues.hospitalizationTo;
        }

        return "";
      }
    }
  });

  resetRepresentingDoctor = createDecorator({
    field: 'representingDoctor',
    updates: {
      representingDoctorId: (fieldValue, allValues?: Partial<UpsertTreatmentDto>) => {
        return !fieldValue || !allValues ? undefined : allValues.representingDoctorId;
      }
    }
  });

  resetReferralDoctor = createDecorator({
    field: 'referralDoctor',
    updates: {
      referralDoctorId: (fieldValue, allValues?: Partial<UpsertTreatmentDto>) => {
        return !fieldValue || !allValues ? undefined : allValues.referralDoctorId;
      }
    }
  });

  resetGuarantor = createPmedDecorator(
      {
        field: 'guarantorId',
        updateOnPristine: false,
        updates: {
          bill: (fieldValue) => {
            return isNotNull(fieldValue) ? BillReceiver.GUARANTOR : BillReceiver.PATIENT;
          },
          guarantorDoctor: (fieldValue) => {
            return isNotNull(fieldValue);
          }
        }
      },
      {
        field: 'guarantorDoctor',
        updateOnPristine: false,
        updates: {
          bill: (fieldValue) => {
            return !fieldValue ? BillReceiver.PATIENT : BillReceiver.GUARANTOR;
          },
          guarantorId: (fieldValue, allValues?: Partial<UpsertTreatmentDto>) => {
            return !fieldValue || !allValues ? undefined : allValues.guarantorId;
          }
        }
      },
      {
        field: 'bill',
        updateOnPristine: false,
        updates: {
          docPrinted: (fieldValue, allValues?: Partial<UpsertTreatmentDto>) => {
            return !!(allValues && allValues.bill === BillReceiver.PATIENT);
          }
        }
      });

  unfitDateDecorator = createDecorator({
    field: /^unfit\[.*]\.fromDate$/,
    updates: (value, name, allValues?: Partial<UpsertTreatmentDto>) => {

      if (allValues) {

        const unfitIndex = parseInt(name.replace(/\D/g, ""));

        let unfit = allValues.unfit ? [...allValues.unfit] : [];

        if (unfit[unfitIndex]) {
          const fromDate = value && new Date(value);
          const toDate = unfit[unfitIndex].toDate && new Date(unfit[unfitIndex].toDate);

          if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
            unfit[unfitIndex].toDate = unfit[unfitIndex].fromDate;
          }

          return {
            unfit
          };
        }
      }

      return {};
    }
  });

  addToTreatmentCodes = (form: FormApi) => (code: CodeDto) => {
    const treatmentCode = {
      codeId: code.id,
      code: code.codeId,
      description: code.description
    };

    form.mutators.push("treatmentCodes", treatmentCode);
  };

  addAllToTreatmentCodes = (form: FormApi) => (codes: Array<CodeDto>) => {
    const mappedCodes = codes.map(code => ({
      codeId: code.id,
      code: code.codeId,
      description: code.description
    }));

    form.mutators.addAllTreatmentCodes(mappedCodes);
  };

  addDiagnosisMutator = (args: any, state: MutableState, utils: Tools) => {
    utils.changeValue(state, 'diagnosis', () => {
      if (state.lastFormState!.values.diagnosis == null) {
        return args[0];
      }
      // TODO kjunuzov 2020-07-03: This case is very unlikely to happen but if it does happen than be sure to
      // notify the user that can't add more diagnoses
      if (state.lastFormState!.values.diagnosis.length > 58) {
        return state.lastFormState!.values.diagnosis;
      }
      if (!state.lastFormState!.values.diagnosis.includes(args[0])) {
        return state.lastFormState!.values.diagnosis + " " + args[0];
      }
      return state.lastFormState!.values.diagnosis;
    })
  };

  addAllTreatmentCodesMutator = (args: any, state: MutableState, utils: Tools) => {
    utils.changeValue(state, 'treatmentCodes', () => {
      return [...state.lastFormState!.values.treatmentCodes, ...args[0]]
    })
  };

  removeTreatmentCodeMutator = (args: any, state: MutableState, utils: Tools) => {
    utils.changeValue(state, 'treatmentCodes', () => {
      return state
          .lastFormState!
          .values
          .treatmentCodes
          .filter((treatmentCode: TreatmentCodeDto) => treatmentCode.codeId != args[0].codeId);
    })
  };

  removeAllTreatmentCodesMutator = (args: any, state: MutableState, utils: Tools) => {
    utils.changeValue(state, 'treatmentCodes', () => {
      return []
    })
  };

  validate = (values: Partial<UpsertTreatmentDto>): Partial<Errors> => {
    return {
      referralDoctorId: values.referralDoctor && !values.referralDoctorId ? "Required" : undefined,
      representingDoctorId: values.representingDoctor && !values.representingDoctorId ? "Required" : undefined
    };
  };

  handleSubmit = async (values: Partial<UpsertTreatmentDto>) => {

    const {cancelTokenSource, tempTreatmentId} = this.state;
    const {options} = this.props;

    if (values.unfit) {
      values.unfit = values.unfit.filter(unfit => {
        if (unfit.fromDate != "" && unfit.toDate != "" && unfit.percent >= 0 && unfit.percent <= 100) {
          return unfit;
        }
      });
    }

    if (!isValueEmpty(values.diagnosis)) {
      let diagnosis = values.diagnosis;

      const diagnosisArr = diagnosis!.split(" ").filter((val) => val != "");

      diagnosis = diagnosisArr.join(" ");

      values.diagnosis = diagnosis;
    } else {
      values.diagnosis = undefined;
    }

    if (tempTreatmentId) {
      values.tempTreatmentId = tempTreatmentId;
    }

    if (values.costEstimationId === -1) {
      values.costEstimationId = undefined;
    }

    values.isTreatmentInterrupted = !!values.isTreatmentInterrupted;
    values.treaterId = options.currentTreater!.id;

    const {treatment, toggleUpsertModal, addTreatment, editTreatment: modifyTreatment} = this.props;
    if (!treatment || treatment.id == -1) {
      await insertTreatment(values, cancelTokenSource)
          .then((response) => {
            toggleUpsertModal();
            addTreatment(response);
          })
          .catch(this.handleError);
    } else {
      await editTreatment(treatment.id, values, cancelTokenSource)
          .then((response) => {
            toggleUpsertModal();
            modifyTreatment(response);
          })
          .catch(this.handleError);
    }
  };

  handleError = (e: any) => {
    const {getMessage} = this.props;

    const error = e.response.data;
    const errorCode = error.errorCode;

    const knownErrors: Array<string> = [
      errorUtils.canNotUndeleteTreatment,
      errorUtils.invalidDate
    ];

    const violations: Array<any> = error.violations;
    let knowErrorFound = false;
    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          knowErrorFound = true;
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`))
        }
      });
    }

    if (!knowErrorFound) {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`))
      } else {
        this.setErrorMessage(getMessage('error.general'));
      }
    }
  };

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage: errorMessage
    })
  };

  render(): React.ReactNode {

    const {loadingMessage} = this.state;

    return loadingMessage
        ? <LoaderComponent
            message={loadingMessage}
            dimmerProps={{active: true, inverted: false, page: true}}
        />
        : this.renderModal();
  }

  renderModal = (): React.ReactNode => {

    const {treatmentFormOpen, treatment, toggleUpsertModal, getMessage} = this.props;

    return (
        <Modal
            open={treatmentFormOpen}
            onClose={toggleUpsertModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>
            {treatment ? treatment.readOnly ? getMessage('treatment.info')
                    : getMessage('treatment.edit')
                : getMessage("treatment.add")}
          </Modal.Header>
          <FormModalContentContainer>
            <FinalForm
                onSubmit={this.handleSubmit}
                validate={this.validate}
                subscription={{pristine: true, values: true, submitting: true}}
                decorators={[
                  this.billPeriodByTariff, this.resetHospitalization,
                  this.resetRepresentingDoctor, this.resetReferralDoctor,
                  this.resetGuarantor, this.resetHospitalizationTo,
                  this.unfitDateDecorator
                ]}
                mutators={{
                  ...arrayMutators,
                  addDiagnosis: this.addDiagnosisMutator,
                  addAllTreatmentCodes: this.addAllTreatmentCodesMutator,
                  removeTreatmentCode: this.removeTreatmentCodeMutator,
                  removeAllTreatmentCodes: this.removeAllTreatmentCodesMutator
                }}
                render={this.renderTreatmentForm}
                initialValues={treatment ? treatment : this.initialTreatmentValues()}
            />
          </FormModalContentContainer>
        </Modal>
    )
  };

  renderTreatmentForm = ({handleSubmit, values, form, submitting}: FormRenderProps) => {

    const {
      treatment,
      currentPatientId,
      currentConsultations,
      getMessage
    } = this.props;

    const {
      tariffs,
      reasons,
      costCenters,
      initialGuarantors,
      initialDoctors,
      billReceivers,
      costEstimations,
      treatmentDate,
      isPatientUnderaged,
      errorMessage
    } = this.state;

    const tariffOptions: Array<DropdownOption> = tariffs.map(tariff => ({
      key: tariff.id,
      text: tariff.description,
      value: tariff.id,
      disabled: (tariff as TreatmentTariffDto).tariffDisabled
    }));

    const hospitalizationDateDisabledBefore =
        values.hospitalizationFrom && new Date(values.hospitalizationFrom);

    const selectedTariffs = tariffs.filter(tariff => {
      if (values.tariffId === tariff.id) {
        return tariff;
      }
    });

    const selectedTypeCode =
        selectedTariffs && selectedTariffs.length > 0 ? selectedTariffs[0].typeCode : "";

    const parentInfoMissing = isPatientUnderaged ? !values.parent : false;

    return (
        <form onSubmit={handleSubmit}>
          <FormContainer>
            <FormContent>
              {errorMessage
                  && (
                      <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                        {errorMessage}
                      </StyledErrorMessage>
                  )
              }
              <PraxFormGrid>
                <PraxFormScrollableContainer height="645px">
                  <TreatmentInfoSegment
                      tariffs={tariffOptions}
                      reasons={reasons}
                      costCenters={costCenters}
                      costEstimations={costEstimations}
                      readOnly={treatment && treatment.readOnly}
                      tariffId={values.tariffId}
                      patientId={currentPatientId}
                      treatment={treatment}
                      treatmentDate={treatmentDate}
                      currentConsultations={currentConsultations}
                      isPatientUnderaged={isPatientUnderaged}
                  />
                  <BillOptionsSegment
                      billReceivers={billReceivers}
                      guarantors={initialGuarantors}
                      guarantorId={values.guarantorId}
                      readOnly={treatment && treatment.readOnly}
                      tariffType={selectedTypeCode}
                  />
                  <StyledNotesContainer>
                    <Grid.Row>
                      <Grid.Column width={3}>
                        {getMessage('treatmentForm.notes')}
                      </Grid.Column>
                      <Grid.Column width={13}>
                        <Field
                            name="notes"
                            component={TextArea}
                            rows={2}
                            fluid
                        />
                      </Grid.Column>
                    </Grid.Row>
                  </StyledNotesContainer>
                  <HospitalizationAndDoctorAccordion
                      treatment={treatment}
                      initialDoctors={initialDoctors}
                      disabledBefore={hospitalizationDateDisabledBefore}
                  />
                  <UnableToWorkSegment
                      unfit={values.unfit ? values.unfit : undefined}
                      percents={percent}
                      readOnly={treatment && treatment.readOnly}
                  />
                  <PrescriptionsSegment
                      form={form}
                      treatmentDate={values.treatmentDate ? values.treatmentDate : treatmentDate}
                      readOnly={treatment && treatment.readOnly}
                      parentInfoMissing={parentInfoMissing}
                  />
                  <DiagnosisSegment
                      diagnosis={treatment ? treatment.diagnosis : undefined}
                      addDiagnosis={form.mutators.addDiagnosis}
                      readOnly={treatment && treatment.readOnly}
                  />
                  <TreatmentCodesAccordion
                      addToTreatmentCodes={this.addToTreatmentCodes(form)}
                      addAllToTreatmentCodes={this.addAllToTreatmentCodes(form)}
                      removeTreatmentCode={form.mutators.removeTreatmentCode}
                      removeAllTreatmentCodes={form.mutators.removeAllTreatmentCodes}
                      readOnly={treatment && treatment.readOnly}
                  />
                  <TreatmentPrescriptionSegment
                      treatment={treatment}
                      setUploading={this.setUploading}
                      readOnly={treatment && treatment.readOnly}
                      setTempTreatmentId={this.setTempTreatmentId}
                  />
                </PraxFormScrollableContainer>
              </PraxFormGrid>
            </FormContent>

            <FormButtonsRow>
              {this.renderButtons(submitting)}
            </FormButtonsRow>
          </FormContainer>
        </form>
    );
  };

  renderButtons = (submitting: boolean) => {
    const {
      treatment,
      getMessage,
      toggleUpsertModal,
      selectedTreatment,
      currentUser
    } = this.props;
    const {uploading} = this.state;

    const addMode = !treatment || treatment.id == -1;

    const isDeletedTreatment = treatment && treatment.deleted === 1;
    const disableDeleteButton =
        !selectedTreatment
        || (!addMode && BillStatus.OPEN != selectedTreatment.billStatus)
        || (!addMode && currentUser && currentUser.userConfig.mandateId !== selectedTreatment.mandateId);

    return (treatment && treatment.readOnly) ? (
        <>
          <Button compact
                  type="button"
                  secondary
                  onClick={toggleUpsertModal}
                  disabled={submitting}
          >
            {getMessage('button.close')}
          </Button>
          {
              isDeletedTreatment
              &&
              <ConfirmActionPopup
                  renderTarget={
                    <Button
                        compact
                        type="button"
                        style={{float: "right"}}
                    >
                      {getMessage("button.undelete")}
                    </Button>
                  }
                  onConfirmAction={() => this.undeleteTreatment(treatment)}
                  message={getMessage('treatment.undelete.message')}
                  style={{float: "right", left: "56rem"}}
                  position="top right"
              />
          }
        </>

    ) : (
        <>
          <Button compact primary type="submit"
                  disabled={submitting || uploading}>
            {getMessage('button.ok')}
          </Button>
          <Button compact
                  type="button"
                  secondary
                  onClick={this.onCancelClick}
                  disabled={submitting || uploading}
          >
            {getMessage('button.cancel')}
          </Button>
          <ConfirmActionPopup
              renderTarget={
                <Button
                    compact
                    type="button"
                    style={{float: "right"}}
                    disabled={disableDeleteButton}
                >
                  {getMessage("msgDelete")}
                </Button>
              }
              onConfirmAction={() => this.deleteTreatment(treatment)}
              message={getMessage('treatment.delete.message')}
              style={{float: "right", left: "56rem"}}
              position="top right"
          />
        </>
    );
  };

  onCancelClick = () => {
    const {toggleUpsertModal, getMessage} = this.props;
    const {tempTreatmentId, cancelTokenSource} = this.state;

    if (tempTreatmentId) {
      this.setState({loadingMessage: getMessage("treatmentPrescription.deleting")});
      deleteTempTreatmentPrescription(tempTreatmentId, cancelTokenSource)
          .then(() => noop)
          .finally(() => {
            toggleUpsertModal();
          })
    } else {
      toggleUpsertModal()
    }
  };

  deleteTreatment = (treatment: EditTreatmentValues) => {
    const {toggleUpsertModal} = this.props;
    const {cancelTokenSource} = this.state;

    deleteTreatment(treatment.id, cancelTokenSource)
        .catch((e: any) => this.handleError(e.response.data))
        .finally(() => toggleUpsertModal());
  }

  undeleteTreatment = (treatment: EditTreatmentValues) => {
    const {changeTreatments, toggleUpsertModal} = this.props;
    const {cancelTokenSource} = this.state;

    undeleteTreatment(treatment.id, treatment.patientId, cancelTokenSource)
        .then(response => changeTreatments(response))
        .catch((e: any) => this.handleError(e.response.data))
        .finally(() => toggleUpsertModal());
  }

  setUploading = (uploading: boolean) => {
    this.setState({uploading});
  };

  setTempTreatmentId = (tempTreatmentId?: number) => {
    this.setState({tempTreatmentId});
  };

}

export default withAuthContext(withMessages(TreatmentsForm));
